define((require) => {
    "use strict";

    var $             = require("jquery"),
        _             = require("underscore"),
        Configuration = require("shared/services/Configuration"),
        CONSTANTS     = require("ui/editor/email/Constants"),
        ErrorHandler  = require("shared/services/error/ErrorHandler"),
        ErrorAction   = require("editor/services/error/ErrorAction"),
        ImprovedView  = require("editor/zbone/ImprovedView"),
        {Logger}      = require("shared/util/Logger"),
        Marionette    = require("marionette"),
        RemoveIncompatibleWidgets = require("ui/editor/email/RemoveIncompatibleWidgetsView.js"),
        SyncManager   = require("shared/services/sync/SyncManager"),
        template      = require("editor/ui/notificationbar/NotificationBar.tpl");

    var log = new Logger("NotificationBar");

    var NOTIFICATION_BAR_EXPANDED = "notification-bar-expanded",

        /** Default events for each notification. */
        DEFAULT_EVENTS = {
            // Close button clicked...
            "click .close-icon": function() {
                this.close("manual");
            },
            // Close link clicked, which may still activate an actual link...
            "click .close-notification": function() {
                this.close("manual");
            },
            // Reload/retry/duplicate/community/support action(s)...
            "click .action": function(event) {
                event.preventDefault();
                this._action(event.target);
            },
            // Flip to next notification...
            "click .next-notification": function(event) {
                event.preventDefault();
                this.close("flip"); // TODO: remove focus from link after click - how?
            }
        },

        /** Default options for each notification. */
        DEFAULT_OPTIONS = {

            /**
             * @type {String}
             * The id assigned for the notification, used to ensure that the same logical notification is not
             * shown twice in the same notification queue. An auto assigned id will be generated if none
             * supplied. Could for example be some prefix + experience id to identify an given type of error
             * related to an experience.
             */
            id: null,
            /**
             * @type {Boolean}
             * True to ensure the notification can be closed (default), false otherwise. A non-closeable notification
             * is typically a system wide notification, such as unsupported browser. <p>
             *
             * A non-closeable notification can <b>only</b> be closed explicitly via is notification id, never
             * as part of a close based on tag or something else.
             */
            closeable: true,
            /**
             * @type {Boolean}
             * True to a manual show close button with notification (default), false otherwise.
             * Can also be manual closed via a link in the notification message like
             * <a class="close-notification">Close me</a>.
             */
            showCloseButton: true,
            /**
             * @type {*|String}
             * Map with keys "global", "manual", "delay", "hide", and "flip" with allowed values "all", "self", "tag",
             * or "no", indicating if notifications must be removed on a given close type. For example, "manual" = "self"
             * means that closing a notification via the close button will remove it from the queue of notifications
             * in the bar, while "flip" = "no" means the notification is not removed when the user flips through
             * the notifications manually, while "hide" = "self" means that when the notification is automatically
             * replaced (i.e. non user triggered) by another one, it should be removed. <p>
             *
             * If a string, each key will be assigned the string value.
             */
            clearOnClose: {
                global: "all", manual: "self", delay: "self", hide: "no", flip: "no"
            },
            /**
             * @type {Boolean}
             * True to show queued notification count (default), false otherwise. If shown, the count
             * is clickable to flip through messages. Each message manually closed will be removed
             * from the queue; if no manual close button, it will be removed on flipping.
             */
            showCount: true,
            /**
             * @type {Number}
             * Optional delay in ms before the current notification is closed (default none).
             * If queued notifications, the next one will be shown after delay.
             */
            closeDelay: undefined,
            /**
             * @type {Boolean}
             * True to ignore a notification if it has the same content as an already queued
             * notification (default), false otherwise.
             */
            ignoreDuplicate: true,
            /**
             * @type {Boolean}
             * True for removing the incompatible widgets in email creator
             * notification, false (default) otherwise.
             */
            removeIncompatibleWidgets: false,
            /**
             * @type {Boolean}
             * True to queue notification for later display (default), false otherwise. If false,
             * the notification will be shown immediately.
             */
            queue: true,
            /**
             * @type {*|Boolean}
             * Map with keys "hide", "flip", "queue", "delay", and "duplicate" and boolean values, where a true value
             * indicates that the notification bar should flash on the given event. For example, "flip" = true means
             * the notification bar will flash when the user flips to the next notification, while "hide" = false
             * means no flashing if the notification is replaced by another one getting focus (non user triggered). <p>
             *
             * If a boolean, each key will be assigned the boolean value.
             */
            flash: {
                hide: true, flip: true, queue: true, duplicate: true, delay: true
            },
            /**
             * @type {Boolean}
             * True if the notification is an error, false otherwise (default). Errors are styled differently.
             */
            error: false,
            /**
             * @type {Boolean}
             * True to alert the notification error via <tt>window.alert(..)</tt> if and only if the log level
             * is <tt>Logger.VERBOSE</tt>, false otherwise (default). The error will still be notification as
             * a normal notification error as well. <p>
             *
             * An error with the <tt>options.alert</tt> options set to true will only ever be alerted once, and only
             * if no other alert is pending as we do not want to spam the user with alert boxes. Errors with alerts
             * will be displayed immediately if no other alerts are shown at notification time, i.e. flipping from
             * existing notification shown if so.
             */
            alert: false,
            /**
             * @type {Boolean}
             * True to raise the notification as an <tt>Error</tt> one time only, and if and only if
             * <tt>options.error</tt> is also true, false otherwise (default).
             */
            raise: false,
            /**
             * @type {String}
             * A tag to group the notification with similar notifications, so they can be removed together. <p>
             *
             * For example, <tt>options.clearOnClose.manual = "someTag"</tt> will removed all notifications
             * with "someTag" on manual close. If the tag value ends with colon (":"), any notification with
             * the tag prefix will be removed, excluding the colon itself. For example, "someTag:" will remove
             * both "someTagXX" and "someTagYY". <p>
             *
             * If no tag is specified, errors will be given "error" and normal notifications "message" unless
             * a default tag has been specified via <tt>NotificationBar.tag(tag:String)</tt>.
             */
            tag: null,
            /**
             * @type {String}
             * An optional statsD name to be issued when the notification is opened, queued, or closed, and the actual
             * statsD name will be <tt>notificationbar.open</tt>, <tt>notificationbar.queue</tt>, and
             * <tt>notificationbar.close</tt>, respectively. If no specified, the <tt>options.tag</tt> will be
             * used to track said events.
             */
            statsD: null,
            /**
             * @type {{nameAndSelector:String, handler:Function}}
             * Hook for events to add to this notification bar for the specific notification in jQuery style.
             */
            events: undefined
        },

        /**
         * @type {Number}
         * Counter for auto assigned id for a notification where no explicit id has been assigned on show.
         */
        ID = 0,

        /**
         * @type {Number}
         * The delay in ms for the flash duration on flash. Should be (a bit) larger than the CSS class "flash" used
         * or the flash animation will never complete before removed again.
         */
        FLASH_DURATION = 300;


    /**
     * @class Notification
     * A <i>notification</i> describes a message, normal or error, shown by this notification bar.
     *
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags.com</a>
     */
    var Notification = function(content, options) {
        /** @type {String} The id of this notification. */
        this.id = (options.id || ++ID) + "";
        /** @type {{message:String, ..}} The formatted content of this option, can also be an error. */
        this.content = content;
        /** @type {*} The options for this notification. */
        this.options = options;
        /** @type {Number} The creation time is ms since the epoch, client time. */
        this.timestamp = Date.now();

        // Must show queue count if non-closeable, since other notifications would be hidden forever. Still allowed
        // to be removed via "id", e.g. on delay...
        if (!this.options.closeable) {
            this.options.showCloseButton = false;
            this.options.showCount = true;
        }

        this.toString = function() {
            var s = "Notification[id: " + this.id;
            if (this.options.tag) {
                s += "; tag: <" + this.options.tag + ">";
            }
            if (!this.options.closeable) {
                s += "; non-closable";
            }
            s += "; " + (this.options.error ? "error" : "message") + ": " + this.content.message + "]";
            return s;
        };
    };


    /**
     * Global notification bar that sits on top of the main content section (just below the top navigation bar).
     *
     * <pre>
     *
     * this.viewScope.showNotification("Hello notification bar - close me <a class='close-notification'>here</a>", {
     *     onClose: function () {
     *         console.log("I was just closed...");
     *     },
     *     showCloseButton: false,
     *     closeDelay: 5000 // Closing in 5 seconds
     * });
     *
     * </pre>
     *
     * @class NotificationBar
     *
     * @author Kim Knudsen / <a href="http://www.zmags.com">Zmags.com</a>
     * @author Gunni Rode / <a href="http://www.zmags.com">Zmags.com</a>
     */
    var NotificationBar = ImprovedView.extend({

        template: template,

        /**
         * @type {HTMLElement}
         * The HTMLElement defined in index.html
         */
        el: ".notification-bar",

        ui: {
            content:            ".content",
            messageContent:     ".message",
            nextNotification:   ".next-notification"
        },

        regions: {
            removeIncompatibleWidgetsRegion: ".remove-incompatible-widgets-region"
        },

        /**
         * The template helpers will at render time be set to the options for <tt>this._notification</tt>, will
         * be set to the options for the opened notifications plus the "action" property.
         */
        templateHelpers: {showCloseButton: false, action: "", request: ""}, // properties known to be referenced there...

        events: DEFAULT_EVENTS,

        /**
         * @private @type {Notification[]}
         * Queued notification messages, ordered according to queue time. <p>
         */
        _notifications: [],

        /**
         * @private @type {Notification}
         * The current notification from <tt>this._notifications</tt> that is active (and thus shown if open).
         */
        _notification: null,

        /**
         * @private @type {Number}
         * Timestamp of when this notification bar was last opened or flashed, to prevent flashing on notifications
         * queued closely together in time, e.g. on multiple variant failures from "one save".
         */
        _timestamp: Date.now(),

        /**
         * @private @type {Boolean}
         * True if an error is currently also alerted via <tt>alert(..)</tt>, false otherwise. <p>
         */
        _alertOpen: false,

        /**
         * @private @type {String}
         * The current default tag to assign to each notification without a tag. <p>
         *
         * This can change over time via <tt>NotificationBar.tag(tagOrView:String|ImprovedView)</tt>, for example a
         * given tag only relevant when in preview mode. <p>
         *
         * If no tag is set here, errors and normal notifications will be tagged with "error" and "message",
         * respectively.
         */
        _tag: null,

        popupModel: null,

        //

        /**
         * Opens the supplied notification, regardless if from the queue or not.
         *
         * @param  {Notification} notification The notification to open; never null.
         * @return {String} The id of <tt>notification</tt>; never null.
         */
        _open: function(notification) {
            var options = notification.options;
            // Set the current notification...
            this._notification = notification;

            var model = notification.content.model,
                action = notification.content.action || "",
                noDuplicate = action.indexOf(ErrorHandler.Actions.DUPLICATE) !== -1,
                request = "";

            if (model) {
                request = model.syncState.current.method || model.syncState.actual.method;
                // We only support duplicate for Experiences for now...
                noDuplicate = noDuplicate && model.__typeName !== "Experience";
            }

            // Remove duplicate if a non-Experience model or if no model at all...
            if (noDuplicate) {
                action = action.replace(ErrorHandler.Actions.DUPLICATE, "");
            }

            // Make the options object available to the template...
            this.templateHelpers = _.extend({}, options);
            this.templateHelpers.action = action;
            this.templateHelpers.request = request;

            // Add events based on options as well...
            this._addEvents(options);

            // Re-render...
            this.render();

            // Actual formatted content, e.g. a normal notification or perhaps an error message...
            var content = this._notification.content.message;

            // Update dom...
            this.ui.messageContent.html(content);
            this._renderQueueCount(options);

            // Make sure events are up to date with the current UI
            this.delegateEvents();

            var root = $("html");
            root.addClass(NOTIFICATION_BAR_EXPANDED);
            this.$el.removeClass("closed").addClass("open");

            // Do we need to alert and/or raise the error?
            if (options.error) {
                // Tag with "error" class...
                this.ui.content.addClass("error");

                // Alert?
                if (options.alert) {
                    this._alert(notification);
                }
                // Raise?
                if (options.raise) {
                    options.raise = false;
                    log.error("Raising notification: " + notification);
                    throw new Error(content);
                }

            // Ensure not tagged with "error" class...
            } else {
                this.ui.content.removeClass("error");
            }

            log.verbose("Opening notification: " + notification);

            // Track open...
            this._track("open", options);

            return this._notification.id;
        },

        /**
         * Alerts the supplied notification via <tt>window.alert</tt> at most one time, and only if no alert is
         * currently shown.
         *
         * @param {Notification} notification The notification to potentially alert via <tt>windows.alert</tt>.
         */
        _alert: function(notification) {
            // Never alert this again, even if we do not alert now if the alert box is already opened!
            notification.options.alert = false;
            if (this._alertOpen) {
                return;
            }
            this._alertOpen = true;

            // Generate message based on error options...
            var error = notification.content,
                msg = error.message,
                scope = this;

            if (error.action) {
                if (error.action.indexOf(ErrorHandler.Actions.RELOAD) !== -1) {
                    msg += "\n\nPlease refresh before you continue your work!";
                }
                if (error.action.indexOf(ErrorHandler.Actions.RETRY) !== -1) {
                    msg += "\n\nPlease try again!";
                }
                // No need to alert info about the community in an alert box...
                if (error.action.indexOf(ErrorHandler.Actions.SUPPORT) !== -1 && Configuration.supportEmail) {
                    msg += "\n\nPlease contact " + Configuration.supportEmail + " if you need further assistance.";
                }
            }

            // Let relevant views update themselves before we alert and stop the world...
            setTimeout(() => {
                alert(msg);
                // Do not clear flag until pending work has been allowed to execute or the next one will alert again
                // immediately...
                setTimeout(() => { scope._alertOpen = false; }, 50); // 50 - let other (0) timeouts complete before this one...
            }, 50); // ditto
        },

        /**
         * Adds a flash effect to this notification bar if so configured via the supplied options for the specified
         * transition type, e.g. on "hide" or "flip". <p>
         *
         * For a flash effect to be applied to this notification bar, the following must hold:
         *
         * - No <tt>window.alert</tt> is shown, or pending to be shown immediately.
         * - <tt>options.flash[type]</tt> is truthy
         * - A flash effect has not been applied within the last FLASH_DURATION * 3 ms
         * - A flash effect is not already applied to this notification bar.
         *
         * @param {*}      options The notification options; never null.
         * @param {String} type    The notification transition type to potentially add the flash effect for if
         *                         so configured in <tt>options</tt>, e.g. "hide" or "flip"; never null.
         *
         * @return {Boolean} True if a flash will be applied, false otherwise.
         */
        _flash: function(options, type) {
            var now = Date.now();
            if (!this._alertOpen && options.flash[type] && !this.$el.hasClass("flash")) {
                // Prevent flashing if we for example queue multiple notifications just after each other...
                if (now - this._timestamp > FLASH_DURATION * 3) {
                    this._timestamp = now;
                    this.$el.addClass("flash");
                    setTimeout(() => { this.$el.removeClass("flash"); }, FLASH_DURATION);
                    return true;
                }
            } else {
                this._timestamp = now;
            }
            return false;
        },

        _action: function(element) {
            var notification = this._notification,
                action = $(element).parent()[0].className,
                done; // "reload", "duplicate", "retry", "community", "removeIncompatibleWidgets" or "support"...

            // Special case for duplicate as we might want to continue editing another (new) model. At this point,
            // we know we only have an Experience, since the DUPLICATE action will have been removed for other
            // model types when opening the notification...
            if (action === ErrorHandler.Actions.DUPLICATE) {
                // We do not need a fail handler, since failures to save (create) an improved model is already
                // notified via this notification bar via the error handler... :)
                done = function(type, clone) {
                    // Do no give the action to duplicate again for the current notification since we just did
                    // exactly that...
                    notification.content.action = notification.content.action.replace(action, "");
                    var options = {
                        tag:        "duplicate_created",
                        queue:      false,
                        closeDelay: 60000,
                        events: {
                            "click .duplicate": function() {
                                // Remove notification about conflict and this notification, since the user just
                                // clicked the link to edit the duplicate experience...
                                this.close({id: notification.id});
                                this.close({tag: "duplicate_created"});
                            }.bind(this)
                        }
                    };
                    this.open({message: "Copy created: \"" + clone.distinguishedName() + "\". <a class='duplicate' href='#experience/" + clone.get("id") + "/edit'>Click to edit it</a> - your changes here will be lost.", model: clone}, options);
                }.bind(this);
            } else if (action === ErrorHandler.Actions.RELOGIN) {
                // Only do this once to avoid flooding the server with futile relogin attempts...
                notification.content.action = notification.content.action.replace(action, "");
            } else if (action === ErrorHandler.Actions.REMOVE_INCOMPATIBLE_WIDGETS) {
                this.removeIncompatibleWidgetsRegion.show(new RemoveIncompatibleWidgets({
                    model: this.popupModel
                }));
                this.popupModel.set({state: CONSTANTS.OPEN});
            }

            // Will also work if no model. If a model, we already have a listener for it to close
            // the notification on successful syncing. However, on "duplicate", we will have a new model, so
            // we have test for that and notify the user once done...
            ErrorAction.forError(notification.content, action)("notificationbar", done);
        },

        _track: function(type, options) {
            if (this.options.statsD) {
                this.options.statsD.track("notificationbar." + type + "." + (options.statsD || options.tag));
            }
        },

        //

        _handleOptions: function(options) {
            options = options || {};
            var o = options.clearOnClose;
            // Handle option short cuts and nested options...
            if (_.isString(o)) {
                options.clearOnClose = {
                    global: o, manual: o, hide: o, flip: o, delay: o
                };
            } else if (o) {
                _.defaults(o, DEFAULT_OPTIONS.clearOnClose);
            }
            o = options.flash;
            if (_.isBoolean(o)) {
                options.flash = {
                    queue: o, hide: o, flip: o, duplicate: o
                };
            } else if (o) {
                _.defaults(o, DEFAULT_OPTIONS.flash);
            }

            // Add default tag if none supplied, e.g. makes it easier to remove all errors, for example...
            if (!options.tag) {
                options.tag = this._tag || (options.error ? "error" : "message");
            }

            // Store a reference to the current options
            return _.defaults(options, DEFAULT_OPTIONS);
        },

        _parseContent: function(content, options) {
            if (_.isFunction(content)) {
                return {message: content.call(this, options)};
            } else if (_.isString(content)) {
                return {message: content};
            }
            // {message:String, ..}, e.g. an error...
            return content;
        },

        _clearDelayTimer: function(options) {
            if (options.timer) {
                clearTimeout(options.timer);
                options.timer = null;
            }
        },

        _setDelayTimer: function(options) {
            if (_.isNumber(options.closeDelay)) {
                options.timer = setTimeout(() => {
                    this._clearDelayTimer(options);
                    this.close("delay");
                }, options.closeDelay);
            }
        },

        _addEvents: function(options) {
            this._setDelayTimer(options);
            if (_.isObject(options.events)) {
                this.events = _.extend(DEFAULT_EVENTS, options.events);
            }
        },

        _renderQueueCount: function(options) {
            // -1 since we always have all notifications to the queue, including the one shown...
            var length = options.showCount && (this._notifications.length - 1);
            this.ui.nextNotification.html(length ? length + " more..." : "");
        },

        _matches: function(notification, ignoreDuplicate) {
            var i,
                l,
                n;

            // This works because all notifications are queue in "this._notifications", even the one currently shown...
            for (i = 0, l = this._notifications.length; i < l; i++) {
                n = this._notifications[i];
                if (n.id === notification.id) {
                    return n;
                // Ignore duplicates? If so, the content *and* tag must match...
                } else if (ignoreDuplicate && (n.options.tag === notification.options.tag) && (n.content.message === notification.content.message))  {
                    return n;
                }
            }
            return null;
        },

        //

        /**
         * Sets the default tag for this notification bar. <p>
         *
         * Any notification without an explicit tag will be tagged with the supplied tag. If falsey, errors and
         * normal notifications will be tagged with "error" and "message" henceforth, respectively. <p>
         *
         * If the supplied tag is a view, a unique tag is generated and used until said view is closed.
         *
         * @param {String|ImprovedView} [tagOrView] The default tag for any notification without a tag; can be falsey,
         *                                          which will clear the already set default tag.
         */
        tag: function(tagOrView) {
            var tag = tagOrView;
            if (tagOrView instanceof Marionette.Layout) {
                tag = "view" + Date.now(); // cid may not be ready
                this.listenToOnce(tagOrView, "close", () => {
                    // Clear tag if not overridden by another context/view...
                    if (this._tag === tag) {
                        this.tag();
                    }
                    // Remove notifications with it...
                    this.close({tag: tag, clear: "tag"});
                });
            }
            this._tag = tag;
            log.info("Setting default tag: <" + (tag || "") + ">");
        },

        /**
         * Opens/shows the notification error with the specified content, or enqueues it for later viewing depending
         * on <tt>options</tt> and if this notification bar is currently opened or not. <p>
         *
         * This is merely syntactic sugar for setting <tt>options.error = true</tt> and then calling
         * <tt>this.open(content, options)</tt>. <p>
         *
         * If no <tt>options.tag</tt> is specified, the tag "error" will be set for the notification. <p>
         *
         * If <tt>content</tt> is an object like an error, this notification bar will recognise a
         * <tt>content.action</tt> value of "reload", "retry", "community", or "support", or a combination thereof,
         * and add a relevant link to such an action when the notification is shown. See <tt>ErrorHandler.Actions</tt>.
         *
         * @param  {String|*|Function} content A string message (HTML supported), a template function that will be
         *                                     invoked with the options as the only argument, or a notification object
         *                                     like <tt>{message:String, action:String, ..}</tt> (complies
         *                                     with errors as dictated by <tt>ErrorHandler</tt>). If falsey, including
         *                                     if the return value from the function is falsey, the notification will
         *                                     be ignored.
         *
         * @param  {*}              [options] Options for configuring the notification; will default if not supplied.
         *
         * @return {String} The id assigned to the shown or queued notification error; will be null if neither shown,
         *                  nor queued.
         */
        error: function(content, options) {
            options = options || {};
            options.error = true;
            return this.open(content, options);
        },

        /**
         * Opens/shows the notification with the specified content, or enqueues it for later viewing depending
         * on <tt>options</tt> and if this notification bar is currently opened or not. <p>
         *
         * If closed and no arguments supplied, the last opened one or alternatively the first queued one will be
         * opened, if any. <p>
         *
         * If <tt>content</tt> is an object like an error, this notification bar will recognise a
         * <tt>content.action</tt> value of "reload", "retry", "community", or "support", or a combination thereof,
         * and add a relevant link to such an action when the notification is shown (from <tt>ErrorHandler.Actions</tt>).
         * This is typically the case for errors, but is also applicable for normal notifications. <p>
         *
         * If no <tt>options.tag</tt> is specified, the tag "error" will be set for the notification if
         * <tt>options.error</tt> is true, otherwise the tag will be set to "message". <p>
         *
         * @param  {String|*|Function} [content] A string message (HTML supported), a template function that will be
         *                                       invoked with the options as the only argument, or a notification object
         *                                       like <tt>{message:String, action:String, ..}</tt> (complies
         *                                       with errors as dictated by <tt>ErrorHandler</tt>). If falsey, including
         *                                       if the return value from the function is falsey, the notification will
         *                                       be ignored.
         *
         * @param  {*}               [options] Options for configuring the notification; will default if not supplied.
         *
         * @return {String} The id assigned to the shown or queued notification; will be null if neither shown,
         *                  nor queued.
         */
        open: function(content, options) {
            var opened = this.opened();

            // Re-open if closed the last opened one or take the first queued one otherwise...
            if (!opened && !arguments.length && this._notifications.length) {
                return this._open(this._notification || this._notifications[0]);
            }

            // Merge supplied options with default ones...
            options = this._handleOptions(options);
            // We have to parse the content here, since it might be queued and we want to reflect the value the
            // notification had the time of queuing. We do not add the notification again if duplicate, either
            // based on notification id, or based on duplicate content if so configured in the options.
            content = this._parseContent(content, options);

            // Never alert if already an alert open and only on VERBOSE...
            options.alert = Logger.level() === Logger.VERBOSE && options.alert && !this._alertOpen;

            // Create notification and push it to array of notifications...
            var notification = new Notification(content, options);

            this.popupModel = options.popupModel || this.popupModel;
            // Ignore if no content, or if the configuration content matches that of an already queued notification
            // if so configured via "options.ignoreDuplicate" or id...
            if (!content) {
                log.warn("Ignoring notification with no content");
                return null;
            }
            var existing = this._matches(notification, options.ignoreDuplicate);
            if (existing) {
                log.verbose("Ignoring notification matching existing one: " + notification + " -> " + existing, notification, existing);
                // If the existing one is an error, go to it. Otherwise flash the existing notification, regardless
                // which is match the supplied notification, to signal to the user that something is up...
                if (opened) {
                    // If the existing is already opened, simply flash if so configured...
                    if (existing.options.error && existing !== this._notification) {
                        this.close({type: "duplicate", next: existing});
                        return existing.id;
                    }
                    // Set close delay timer if set, since relevant again, and restart it again...
                    options = existing.options;
                    this._clearDelayTimer(options);
                    this._setDelayTimer(options);

                    // Note: we do show updated actions, i.e. we may have removed some from the existing notification
                    // which we do not want to add again (one time actions)...
                    this._flash(options, "duplicate");
                    return null;
                }
                // If not opened, we open again to notify the user (without flash)...
                return this._open(existing);
            }

            // Always enqueue the notification so we can find it again *and* the next one to display on close...
            this._notifications.push(notification);

            // If we have a model associated with the notification, make sure this notification is removed
            // if the model is synced successfully, for example because of a reload or retry of a save. We
            // also remove other notifications for the same model, since probably no longer relevant. We do
            // this here, even if queued, since we might get notifications for model variants as well in the
            // background...
            var model = notification.content.model;
            if (model) {
                SyncManager.onSync({
                    context:  this,
                    target:   model,
                    callback: function() {
                        this.close({model: model, clear: "model"}); // will also remove this listener!
                    }.bind(this)
                });
            }

            // Close existing one if opened unless we are about to queue...
            if (opened) {
                // Enqueue only? Adhered to options *unless* the current notification is not an error and we are
                // supplying an error now...
                if (options.queue && (this._notification.options.error || !options.error)) {
                    log.verbose("Enqueued notification: " + notification);
                    // Never alert some message we later dequeue...
                    options.alert = false;
                    this._flash(options, "queue");
                    this._renderQueueCount(options);

                    // Track queued...
                    this._track("queue", options);
                    return notification.id;
                }

                // Close existing one first, if any, by doing a hide to the new one about to be shown to adhered to the
                // rules about deleting on different types of closing. We ensure we show the one just pushed by
                // supplied the index of it to close...
                this.close({type: "hide", next: notification});
                return notification.id;
            }

            // No existing one shown, so simply open...
            return this._open(notification);
        },

        /**
         * Returns the current notification opened in this notification bar. <p>
         *
         * If this notification bar is not opened, a falsey value will be returned.
         *
         * @return {Notification} The current notification opened, or a falsey value.
         */
        opened: function() {
            return this.$el.hasClass("open") && this._notification;
        },

        /**
         * Removes all notifications matches by the supplied options, but not not update this notification bar
         * visually or its internal state otherwise. <p>
         *
         * Non-closeable notifications are <b>not</b> affected by this except if deleted by id explicitly! <p>
         *
         * This will also clear the current active one if it was removed because of it; if this notification bar
         * is open, it will then flip to the next one to be shown. <p>
         *
         * Examples:
         * <pre>
         *    // Remove all *closeable* notifications...
         *    var notification = this._remove(); // or this._remove({}) or this._remove({close: false})
         *    // -> "notification" will be the active one just removed, if any
         *
         *    // Remove *all* notifications, regardless if closeable or not (e.g. on logout)...
         *    var notification = this._remove({close: true});
         *    // -> "notification" will be the active one just removed, if any
         *
         *    // Removes the notification with the specified id...
         *    var notification = this._remove({id: "xx"});
         *    // -> "notification" will be the active one just removed if it had the id "xx", otherwise null.
         *
         *    // Removes the notifications with the specified *exact* tag (ignoring case)...
         *    var notification = this._remove({tag: "foo"});
         *    // -> "notification" will be the active one just removed if it had the exact tag "foo", otherwise null.
         *
         *    // Removes the notifications with the *starting* with the specified tag...
         *    var notification = this._remove({tag: "foo:"});
         *    // -> "notification" will be the active one just removed if it had a tag starting with "foo", otherwise null.
         *    ..
         *
         *    // Via value from <tt>options.clearOnClose</tt> when called as part of the close flow, e.g.
         *    // if configured as <tt>options.clearOnClose.manual = "self":
         *
         *    // Remove all...
         *    var notification = this._remove({type: "all"});
         *    // same as this._remove() or this._remove({})
         *
         *    // Removes the current active notification, if any...
         *    var notification = this._remove({type: "self"});
         *    // -> "notification" will be the active one just removed, if any, otherwise null.
         *
         *    // Removes the notifications with the specified *exact* tag (ignoring case)...
         *    var notification = this._remove({type: "foo"});
         *    // -> "notification" will be the active one just removed if it had the exact tag "foo", otherwise null.
         *
         *    // Removes the notifications with the *starting* with the specified tag...
         *    var notification = this._remove({type: "foo:"});
         *    // -> "notification" will be the active one just removed if it had a tag starting with "foo", otherwise null.
         *
         *    // -> "notification" always null, no notifications removed
         *    // Do not remove notifications...
         *    var notification = this._remove({type: "no"});
         *    // -> "notification" always null, no notifications removed
         *    ..
         * </pre>
         *
         * @param {*} [options] The options to remove notifications by. If falsey or <tt>options.clear</tt> = "no",
         *                      no notifications will be removed.  If <tt>options.id</tt> is defined, the notification
         *                      with said id, if any, will be removed (ignoring <tt>options.tag</tt>). If
         *                      <tt>options.tag</tt> is supplied, all notifications with said tag will be removed
         *                      (ignoring case). If <tt>options.tag</tt> ends with a colon (":"), any tag starting with
         *                      the <tt>options.tag</tt> value excluding the colon will be removed ("tag wild card",
         *                      ignoring case). Finally, none of these cases match, all notification will be
         *                      removed.
         *
         * @return {Number} The number of notifications removed.
         */
        _remove: function(options) {
            // Removes all notifications based on the supplied predicate and returns the removed count...
            var remove = function(predicate) {
                var n = this._notifications.length,
                    notification,
                    i = 0;

                while (n--) {
                    notification = this._notifications[n];
                    if (predicate(notification)) {
                        log.verbose("Removing notification: " + notification);
                        this._stopListening(notification);
                        this._notifications.splice(n, 1);
                        // No break here!
                        i++;
                    }
                }
                return i;
            }.bind(this);

            // Number of removed notifications...
            var i = 0;

            // No options, so nothing to remove, but can still be hidden or flipped, for example, so need to be closed...
            if (!options || options.clear === "no") {
                return 0;

            // Remove by id, ignoring any tag. Note that non-closeable notifications *can* be closed based on id...
            } else if (options.id) {
                i = remove((notification) => {
                    return notification.id === options.id; // also non-closeable ones!
                });

            // Remove by tag, or a prefix of such...
            } else if (options.tag) {
                var tag = options.tag,
                    regex;

                // Wild card, e.g. all "concurrent:XXX"...
                if (tag.indexOf(":") === tag.length - 1) {
                    regex = new RegExp("^" + tag.substring(0, tag.length - 1), "i");

                // Exact tag, ignoring case...
                } else {
                    regex = new RegExp("^" + tag + "$", "i");
                }
                i = remove((notification) => {
                    return notification.options.closeable && regex.test(notification.options.tag);
                });

            // Remove all errors for the specified model?
            } else if (options.model) {
                i = remove((notification) => {
                    return notification.options.closeable && notification.content.model === options.model;
                });

            // Empty options, or options.clear = "all" specified, so clear all (closeable) notifications...
            } else {
                var all = options.clear === "all";
                i = remove((notification) => {
                    // A bit of hack, since that is truly not closeable, not even after logout... :)
                    return (all && notification.options.tag !== "unsupported_browser") || notification.options.closeable;
                });
            }
            return i;
        },

        /**
         * @private
         * Stop listening for "sync:state" for the supplied removed notification if it has an associated
         * model.
         *
         * @param {Notification} notification Removed notification; never null, nor empty.
         */
        _stopListening: function(notification) {
            var model = notification.content.model;
            if (model) {
                this.stopListening(model, "sync:state");
            }
        },

        /**
         * Generates usable close options based on the supplied options.
         *
         * @param {*|String} options The close options, or <tt>clearOnClose<tt> type.
         * @return {*} Usable close options for use in <tt>this.close()</tt>.
         */
        _handleCloseOptions: function(options) {
            // Can be a close type like "manual", or close options like {id: xx}...
            if (!options) {
                options = {type: "global"};
            } else if (_.isString(options)) {
                options = {type: options};
            } else if (!options.type) {
                options.type = "global";
            }

            // If no current notification, we use "no" since not really much we can do otherwise. This also means
            // that all cases below testing on "notification" will work since never hit if no notification...
            if (!options.clear) {
                options.clear = this._notification ? (this._notification.options.clearOnClose[options.type] || "no") : "no";
            }


            // Translate close type, if any, i.e. "all", "self", "no", or a tag to relevant options for removal below...
            switch (options.clear) {
                case "all":
                case "no":
                    // Remove all...
                    break;

                case "tag":
                    // Remove via tag on active notification unless a tag has already been specified...
                    if (options.tag) {
                        break;
                    } else if (this._notification && this._notification.options.tag) {
                        options.tag = this._notification.options.tag;
                        break;
                    }
                    // Fall through.

                case "self":
                    // Remove via id or self, e.g. when active...
                    if (!options.id && this._notification) {
                        options.id = this._notification.id;
                        break;
                    }
                    // Fall through.

                case "model":
                    // Remove notifications associated with given model...
                    if (options.model) {
                        break;
                    } else if (this._notification && this._notification.content.model) {
                        options.model = this._notification.content.model;
                        break;
                    }
                    // Fall through.

                default:
                    // Remove via actual tag or tag prefix...
                    options.tag = options.type; // actually a tag here...
                    break;
            }

            return options;
        },

        /**
         * Returns the number of notifications for this bar that has not been removed, e.g. those not removed on
         * close or flip. <p>
         *
         * To open this bar when closed to show existing notifications, simply call <tt>open()</tt> (with no
         * arguments).
         *
         * @return {Number} The number of notifications.
         */
        count: function() {
            return this._notifications.length;
        },

        /**
         * Returns the the list of all notification messages.
         *
         *
         * @return {List} Returns the list active notification messages in queue.
         */
        list: function() {
            let notifications = [];

            this._notifications.map((notification) =>
                notifications.push({
                    id: notification.id,
                    message: notification.content.message
                }));

            return notifications;
        },

        /**
         * Finds the next notification to show, if any, as part of the close flow.
         *
         * @param {*}            closeOptions The close options used; never null.
         * @param {Notification} notification The active notification just closed, if any; can be null, and
         *                                    can at this point still be in the list of notifications or it may
         *                                    not be.
         *
         * @return {Notification} The next notification to show; or null if none is to be showed.
         */
        _next: function(closeOptions, notification) {
            // Invariant: called after removal as part of close logic...

            var n = this._notifications.length,
                i,
                j = -1,
                error = false;

            // No more notifications? Then no next one to show, so we will close the bar entirely...
            if (!n) {
                return null;

            // Next one already specified, i.e. when opening spawns a close of the existing one that then opens
            // the supplied notification....
            } else if (closeOptions.next) {
                return closeOptions.next;

            // If supplied, "notification" is the notification just removed and/or the one currently active...
            } else if (notification) {
                // Make sure we do not select this again as the next notification...
                i = this._notifications.indexOf(notification);
                // Removed, so find non-closeable message, secondarily first error, or message if no errors...
                if (i === -1) {
                    for (i = 0; i < n; i++) {
                        notification = this._notifications[i];
                        // Use first non-closeable one...
                        if (!notification.options.closeable) {
                            j = i;
                            break;

                        // First first error, but may still find a non-closeable one, so no break...
                        } else if (notification.options.error && !error) {
                            error = true;
                            j = i;
                            // No break here

                        // First one then, but may be overruled by non-closeable or error...
                        } else if (!error && j === -1) {
                            j = i;
                        }
                    }

                // Not removed, so take next...
                } else {
                    j = ++i % n;
                }
                return this._notifications[j];
            }
            return null;
        },

        /**
         * Closes this notification bar, and removes relevant notifications based on the supplied
         * close options or <tt>clearOnClose</tt> type. <p>
         *
         * Close options can be a <tt>clearOnType</tt> type from the notification configuration, e.g. "manual",
         * "flip", etc. The active notification will then be removed based on the configured <tt>clearOnClose</tt>
         * type for the current active notification. <p>
         *
         * The close options can also be a query like structure:
         * - {id: XX, clear:String, type:String, ..}:  removes the notification with the specified id and closes
         *   this bar if said notification was the active one.
         * - {tag: XX, clear:String, type:String, ..}: removes all notifications with the specified tag (prefix) and
         *   closes this bar if the active notification was removed because of this.
         * - {model:ImprovedModel}: removes all notification errors with the specified model.
         * - {}: removes all notifications and closes this notification bar.
         *
         * @param {*|String} [closeOptions] Close options, or a <tt>clearOnClose</tt> type.
         */
        close: function(closeOptions) {
            // Can be a close type like "manual", or close options like {id: xx}...
            closeOptions = this._handleCloseOptions(closeOptions);

            // Remove notifications based on close options, which could be zero if <tt>options.clear</tt> = "no", or
            // on no matching notifications, e.g. no such tag found. We will need to update the visible count count
            // in any case if "removed" > 0...
            var removed = this._remove(closeOptions),
                notification = this._notification;

            // No active, bar is already closed, and no need to update queue count either (closed)...
            if (!notification) {
                return;

            // Remove as active if no longer queued, i.e. just removed because of the supplied close options...
            } else if (this._notifications.indexOf(notification) === -1) {
                this._notification = null;

            // We did not remove the active one, so no one to close, but we did remove some other ones, e.g.
            // based on tag, so update the visual queue count. We must always do this if the queue count is
            // visible, since we know we have at least one more notification now, i.e. the active one...
            } else if (removed || closeOptions.clear !== "no") {
                this._renderQueueCount(notification.options);
                return;
            }

            log.verbose("Closing notification: " + notification, closeOptions);

            var options = notification.options;

            // Clear close delay timer...
            this._clearDelayTimer(options);

            // Content may depend on the close handler for each message, so we have to call it even if we immediately
            // show a queued notification...
            if (_.isFunction(options.onClose)) {
                options.onClose();
            }

            // Clean up...
            this.events = DEFAULT_EVENTS;

            // Find the next notification to show, if any. If no such notification, we close this bar now...
            notification = this._next(closeOptions, notification);
            if (notification) {
                this._open(notification);
                // Add (optional) flash effect, since this bar is already open...
                this._flash(notification.options, closeOptions.type);
                return;
            }

            // Track close per type when the active notification was *active* was closed, e.g. to see if the user
            // manually dismisses notification messages...
            this._track("close." + closeOptions.type, options);

            // No next one to show, so close for real...
            var root = $("html");
            root.removeClass(NOTIFICATION_BAR_EXPANDED);

            // Clear dom after animation completes, but only if no other notification has been opened in the meantime...
            setTimeout(() => {
                if (!this._notification) {
                    this.ui.content.html("");
                }
            }, FLASH_DURATION);

            this.$el.removeClass("open").addClass("closed");
        }
    });

    // Return a single (rendered) instance
    var instance = new NotificationBar();
    instance.render();
    return instance;
});



// WEBPACK FOOTER //
// ./src/editor/ui/notificationbar/NotificationBar.js